import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { LOGO_URL, api } from "@/lib/api";

const CURRENCIES = ["CHF", "EUR", "USD", "GBP", "INR"];
const COUNTRIES = [
  { code: "CH", name: "Switzerland" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "IN", name: "India" },
  { code: "SG", name: "Singapore" },
];

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [params] = useSearchParams();
  const ref = params.get("ref");

  // Deep-link: `?ref=<token>` from the growth-nudge Slack share. If the
  // token maps to a live team-share invite, forward the visitor to the
  // accept-invite flow so they land in the right workspace instead of
  // spawning a brand-new one.
  useEffect(() => {
    if (!ref) return;
    (async () => {
      try {
        const { data } = await api.get(`/team/invite-preview/${encodeURIComponent(ref)}`);
        if (data && data.share) {
          navigate(`/accept-invite?token=${encodeURIComponent(ref)}`, { replace: true });
        }
      } catch {
        // Token invalid/expired — silently fall through to normal signup.
      }
    })();
  }, [ref, navigate]);

  const [form, setForm] = useState({
    display_name: "",
    company_name: "",
    email: "",
    password: "",
    country_code: "CH",
    base_currency: "CHF",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Welcome to Lemonade");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-primary p-12 md:flex">
        <Link to="/"><Logo /></Link>
        <div>
          <img src={LOGO_URL} alt="" className="h-40 w-40 rounded-[22%] shadow-2xl" />
          <h2 className="mt-10 max-w-sm font-display text-4xl leading-tight tracking-tight">
            Make your first
            <br />
            <span className="italic">beautiful</span> invoice.
          </h2>
          <p className="mt-4 max-w-sm text-sm text-foreground/70">
            Start free — 5 seats included. Then CHF 20 per additional seat.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.22em] text-foreground/60">Global · Multi‑currency · AI‑native</div>
      </div>

      <div className="flex flex-col justify-center bg-white px-6 py-12 md:px-16">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-10 md:hidden"><Logo /></div>
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Create account</p>
          <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">Start free.</h1>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="signup-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Your name">
                <input
                  required
                  data-testid="signup-name-input"
                  value={form.display_name}
                  onChange={set("display_name")}
                  placeholder="Ava Shah"
                  className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none focus:border-primary"
                />
              </Field>
              <Field label="Company (optional)">
                <input
                  data-testid="signup-company-input"
                  value={form.company_name}
                  onChange={set("company_name")}
                  placeholder="Lemonade Studio"
                  className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none focus:border-primary"
                />
              </Field>
            </div>
            <Field label="Email">
              <input
                type="email"
                required
                data-testid="signup-email-input"
                value={form.email}
                onChange={set("email")}
                placeholder="you@company.com"
                className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none focus:border-primary"
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                required
                minLength={6}
                data-testid="signup-password-input"
                value={form.password}
                onChange={set("password")}
                placeholder="Minimum 6 characters"
                className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none focus:border-primary"
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Country">
                <select
                  data-testid="signup-country-select"
                  value={form.country_code}
                  onChange={set("country_code")}
                  className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none focus:border-primary"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Base currency">
                <select
                  data-testid="signup-currency-select"
                  value={form.base_currency}
                  onChange={set("base_currency")}
                  className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none focus:border-primary"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-button"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-6 py-4 text-sm font-medium text-background hover:bg-foreground/90 active:scale-95 disabled:opacity-60"
            >
              {loading ? "Creating account..." : "Create account"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <p className="mt-8 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-foreground underline underline-offset-4" data-testid="signup-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      {children}
    </label>
  );
}
